/**
 * interruptionBridge
 *
 * Two problems that only exist inside the native shell, solved with one listener.
 *
 * 1. AUDIO SESSION RECOVERY. `audioUnlock.js` already re-blesses the audio
 *    session on `visibilitychange`, `pageshow` and `focus`. A phone call, a Siri
 *    invocation or a CarPlay hand-off does not reliably produce any of those in
 *    WKWebView, so the session can die with no JS-visible event and Timo goes
 *    silent for the rest of the session. We do NOT reimplement the recovery: we
 *    synthesise the `focus` event that `audioUnlock`'s existing hook already
 *    understands, so there is exactly one recovery path to maintain.
 *
 * 2. BACKGROUND MICROPHONE. iOS mutes `microphoneCaptureState` shortly after the
 *    app backgrounds, so a live capture keeps "recording" silence. Subscribers
 *    (see `useNativeCaptureMic`) use `subscribeAppState` to stop cleanly and tell
 *    the user, instead of letting the UI pretend it is still listening.
 *
 * Everything here is inert on the web: `getAppPlugin()` returns null in a browser,
 * `install()` becomes a no-op, and `subscribeAppState` never fires.
 *
 * Deliberately does NOT touch `audioUnlock.js`. That file is load-bearing for the
 * shipping PWA and this module only feeds it events it already handles.
 */

import { isNative } from '@/lib/platform';
import { addAppListener } from './capacitorApp';

const subscribers = new Set();
let installed = false;
let removeListener = null;
let lastActive = true;

/**
 * Subscribe to native foreground / background transitions.
 * @param {(isActive: boolean) => void} cb
 * @returns {() => void} unsubscribe
 */
export function subscribeAppState(cb) {
  if (typeof cb !== 'function') return () => {};
  subscribers.add(cb);
  return () => {
    subscribers.delete(cb);
  };
}

/** True when the app was last known to be in the foreground. Always true on web. */
export function isAppActive() {
  return lastActive;
}

function handleStateChange(isActive) {
  lastActive = isActive === true;

  if (lastActive) {
    // Re-bless through the path audioUnlock already listens on. Dispatching a
    // plain window 'focus' event is intentional: it reuses the existing recovery
    // instead of duplicating the AudioContext resume logic here.
    try {
      window.dispatchEvent(new Event('focus'));
    } catch {
      /* non-fatal */
    }
  }

  subscribers.forEach((cb) => {
    try {
      cb(lastActive);
    } catch {
      /* one bad subscriber must not stop the others */
    }
  });
}

/**
 * Install the bridge once. Safe to call repeatedly and on every platform.
 * @returns {Promise<boolean>} true when a native listener was actually attached
 */
export async function installInterruptionBridge() {
  if (installed) return removeListener !== null;
  installed = true;
  if (!isNative()) return false;

  removeListener = await addAppListener('appStateChange', (state) => {
    handleStateChange(state?.isActive === true);
  });
  return removeListener !== null;
}

/** Test / teardown helper. Not used by app code. */
export function uninstallInterruptionBridge() {
  try {
    removeListener?.();
  } catch {
    /* non-fatal */
  }
  removeListener = null;
  installed = false;
  subscribers.clear();
  lastActive = true;
}
